import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, CreditCard, Zap, ExternalLink, RefreshCw, Calendar, FileText, GraduationCap, Gift, Building2, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useSearchParams } from 'react-router-dom';
import PaymentMethodModal from '../components/PaymentMethodModal';
import PayPalCardModal from '../components/PayPalCardModal';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface User {
  id: number;
  email: string;
  name: string;
  organization_id: number;
  role: string;
  subscription_tier: string;
  credits_remaining: number;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  features: string[];
  employees_limit: number | string;
  credits_limit: number | string;
  highlighted?: boolean;
}

interface Usage {
  conversations_count: number;
  messages_count: number;
  tokens_used: number;
  tasks_count: number;
  documents_count: number;
  credits_remaining: number;
  period_start: string;
  period_end: string;
}

interface Subscription {
  tier: string;
  status: string;
  stripe_subscription_id?: string;
  current_period_end?: string;
}

interface Invoice {
  id: string;
  number: string;
  amount: number;
  status: string;
  pdf_url: string;
  created_at: string;
}

interface PaymentProvider {
  provider: 'stripe' | 'paypal' | null;
  mode: 'test' | 'live';
  clientId: string;
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export default function Billing() {
  const { user, token, updateUser } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [buyingCredits, setBuyingCredits] = useState<number | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider | null>(null);

  // Modal states for PayPal payment method selection
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{
    type: 'subscription' | 'credits';
    plan?: string;
    interval?: 'monthly' | 'yearly';
    creditAmount?: number;
  } | null>(null);

  const currentPlan = user?.subscription_tier || 'free';

  // Load payment provider
  const loadPaymentProvider = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/billing/provider`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json() as APIResponse<PaymentProvider>;
      if (data.success && data.data) {
        setPaymentProvider(data.data);
      }
    } catch (error) {
      console.error('Failed to load payment provider:', error);
    }
  }, [token]);

  // Capture PayPal payment
  const capturePayPalPayment = async (orderId: string) => {
    try {
      const response = await fetch(`${API_URL}/billing/paypal/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      });

      const data = await response.json() as APIResponse<{
        type: string;
        tier?: string;
        amount?: number;
        user?: User;
      }>;

      if (data.success && data.data?.user) {
        updateUser(data.data.user);

        if (data.data.type === 'subscription') {
          setSuccessMessage(`Your ${data.data.tier?.charAt(0).toUpperCase()}${data.data.tier?.slice(1)} subscription has been activated! Credits have been added to your account.`);
        } else if (data.data.type === 'credits') {
          setSuccessMessage(`${data.data.amount?.toLocaleString()} credits have been added to your account!`);
        }

        await Promise.all([loadUsage(), loadSubscription(), loadInvoices()]);
      }
    } catch (error) {
      console.error('Failed to capture PayPal payment:', error);
    }
  };

  // Check for success/cancel from Stripe or PayPal redirect and verify checkout
  useEffect(() => {
    const verifyCheckout = async (sessionId: string) => {
      console.log('Verifying checkout session:', sessionId);
      try {
        const response = await fetch(`${API_URL}/billing/verify-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await response.json() as APIResponse<{
          type: string;
          tier?: string;
          amount?: number;
          user?: User;
        }>;

        console.log('Verify checkout response:', data);

        if (data.success && data.data?.user) {
          console.log('Updating user with:', data.data.user);
          // Update user in store with new data
          updateUser(data.data.user);

          if (data.data.type === 'subscription') {
            setSuccessMessage(`Your ${data.data.tier?.charAt(0).toUpperCase()}${data.data.tier?.slice(1)} subscription has been activated! Credits have been added to your account.`);
          } else if (data.data.type === 'credits') {
            setSuccessMessage(`${data.data.amount?.toLocaleString()} credits have been added to your account!`);
          }

          // Reload all billing data
          await Promise.all([loadUsage(), loadSubscription(), loadInvoices()]);
        } else if (!data.success) {
          console.error('Verify checkout failed:', data.error);
          setSuccessMessage(null);
        }
      } catch (error) {
        console.error('Failed to verify checkout:', error);
      }
    };

    const sessionId = searchParams.get('session_id');
    const paypalToken = searchParams.get('token'); // PayPal uses 'token' parameter for order ID

    // Handle Stripe success
    if (searchParams.get('success') && sessionId) {
      verifyCheckout(sessionId);
      setTimeout(() => setSuccessMessage(null), 8000);
    } else if (searchParams.get('success')) {
      // Fallback for old URLs without session_id - just sync
      syncSubscription();
    }

    if (searchParams.get('credits_success') && sessionId) {
      verifyCheckout(sessionId);
      setTimeout(() => setSuccessMessage(null), 8000);
    } else if (searchParams.get('credits_success')) {
      const amount = searchParams.get('amount');
      setSuccessMessage(`${amount} credits have been added to your account!`);
      loadUsage();
      setTimeout(() => setSuccessMessage(null), 5000);
    }

    // Handle PayPal success
    if (searchParams.get('paypal_success') && paypalToken) {
      capturePayPalPayment(paypalToken);
      setTimeout(() => setSuccessMessage(null), 8000);
    }

    if (searchParams.get('paypal_credits_success') && paypalToken) {
      capturePayPalPayment(paypalToken);
      setTimeout(() => setSuccessMessage(null), 8000);
    }
  }, [searchParams, token]);

  // Sync subscription status with Stripe
  const syncSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/billing/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json() as APIResponse<{
        tier: string;
        status: string;
        user?: User;
      }>;

      if (data.success && data.data?.user) {
        updateUser(data.data.user);
        if (data.data.tier !== 'free') {
          setSuccessMessage(`Your ${data.data.tier?.charAt(0).toUpperCase()}${data.data.tier?.slice(1)} subscription is active!`);
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      }

      // Reload billing data
      await Promise.all([loadUsage(), loadSubscription(), loadInvoices()]);
    } catch (error) {
      console.error('Failed to sync subscription:', error);
    }
  };

  const loadPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/billing/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Plan[]>;
      if (data.success && data.data) {
        setPlans(data.data);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  }, [token]);

  const loadUsage = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/billing/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Usage>;
      if (data.success && data.data) {
        setUsage(data.data);
        // Update user credits in store
        if (data.data.credits_remaining !== user?.credits_remaining) {
          updateUser({ credits_remaining: data.data.credits_remaining });
        }
      }
    } catch (error) {
      console.error('Failed to load usage:', error);
    }
  }, [token, user?.credits_remaining, updateUser]);

  const loadSubscription = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Subscription>;
      if (data.success && data.data) {
        setSubscription(data.data);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  }, [token]);

  const loadInvoices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/billing/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Invoice[]>;
      if (data.success && data.data) {
        setInvoices(data.data);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadPlans(), loadUsage(), loadSubscription(), loadInvoices(), loadPaymentProvider()]);
      setIsLoading(false);
    };
    init();
  }, [loadPlans, loadUsage, loadSubscription, loadInvoices, loadPaymentProvider]);

  const handleUpgrade = async (planId: string) => {
    if (planId === 'free' || planId === currentPlan) return;

    // Wait for payment provider to be loaded
    if (!paymentProvider) {
      console.error('Payment provider not loaded yet');
      return;
    }

    // Check if a provider is configured
    if (!paymentProvider.provider) {
      console.error('No payment provider configured');
      alert('Payment is not available. Please contact support.');
      return;
    }

    // If PayPal is active, show payment method modal
    if (paymentProvider.provider === 'paypal') {
      setPendingPurchase({
        type: 'subscription',
        plan: planId,
        interval: billingInterval,
      });
      setShowPaymentMethodModal(true);
      return;
    }

    // For Stripe, proceed directly to checkout
    setUpgrading(planId);
    try {
      const response = await fetch(`${API_URL}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan: planId,
          interval: billingInterval,
        }),
      });

      const data = (await response.json()) as APIResponse<{ checkout_url: string }>;
      if (data.success && data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else if (!data.success && data.error) {
        console.error('Checkout error:', data.error);
        alert(data.error);
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
    } finally {
      setUpgrading(null);
    }
  };

  const handleBuyCredits = async (amount: number) => {
    // Wait for payment provider to be loaded
    if (!paymentProvider) {
      console.error('Payment provider not loaded yet');
      return;
    }

    // Check if a provider is configured
    if (!paymentProvider.provider) {
      console.error('No payment provider configured');
      alert('Payment is not available. Please contact support.');
      return;
    }

    // If PayPal is active, show payment method modal
    if (paymentProvider.provider === 'paypal') {
      setPendingPurchase({
        type: 'credits',
        creditAmount: amount,
      });
      setShowPaymentMethodModal(true);
      return;
    }

    // For Stripe, proceed directly to checkout
    setBuyingCredits(amount);
    try {
      const response = await fetch(`${API_URL}/billing/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount }),
      });

      const data = (await response.json()) as APIResponse<{ checkout_url: string }>;
      if (data.success && data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else if (!data.success && data.error) {
        console.error('Credits checkout error:', data.error);
        alert(data.error);
      }
    } catch (error) {
      console.error('Failed to buy credits:', error);
    } finally {
      setBuyingCredits(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/billing/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<{ portal_url: string }>;
      if (data.success && data.data?.portal_url) {
        window.location.href = data.data.portal_url;
      } else if (!data.success && data.error) {
        alert(data.error);
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  // Handle payment method selection - Pay with Card
  const handleSelectCard = () => {
    setShowPaymentMethodModal(false);
    setShowCardModal(true);
  };

  // Handle payment method selection - Pay with PayPal (redirect)
  const handleSelectPayPal = async () => {
    setShowPaymentMethodModal(false);

    if (!pendingPurchase) return;

    if (pendingPurchase.type === 'subscription') {
      setUpgrading(pendingPurchase.plan || null);
    } else {
      setBuyingCredits(pendingPurchase.creditAmount || null);
    }

    try {
      const endpoint = pendingPurchase.type === 'subscription'
        ? `${API_URL}/billing/paypal/checkout`
        : `${API_URL}/billing/paypal/credits`;

      const body = pendingPurchase.type === 'subscription'
        ? { plan: pendingPurchase.plan, interval: pendingPurchase.interval }
        : { amount: pendingPurchase.creditAmount };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = (await response.json()) as APIResponse<{ checkout_url: string }>;
      if (data.success && data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else if (!data.success && data.error) {
        console.error('PayPal checkout error:', data.error);
        alert(data.error);
      }
    } catch (error) {
      console.error('Failed to create PayPal checkout:', error);
    } finally {
      setUpgrading(null);
      setBuyingCredits(null);
      setPendingPurchase(null);
    }
  };

  // Handle card payment success
  const handleCardPaymentSuccess = (data: { type: string; tier?: string; amount?: number; user?: User }) => {
    if (data.user) {
      updateUser(data.user);
    }

    if (data.type === 'subscription') {
      setSuccessMessage(`Your ${data.tier?.charAt(0).toUpperCase()}${data.tier?.slice(1)} subscription has been activated! Credits have been added to your account.`);
    } else if (data.type === 'credits') {
      setSuccessMessage(`${data.amount?.toLocaleString()} credits have been added to your account!`);
    }

    // Reload billing data
    Promise.all([loadUsage(), loadSubscription(), loadInvoices()]);

    setTimeout(() => setSuccessMessage(null), 8000);

    // Reset state
    setPendingPurchase(null);
    setShowCardModal(false);
  };

  // Close modals and reset state
  const handleCloseModals = () => {
    setShowPaymentMethodModal(false);
    setShowCardModal(false);
    setPendingPurchase(null);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-accent-green/20 border border-accent-green/30 text-accent-green px-4 py-3 rounded-xl"
        >
          {successMessage}
        </motion.div>
      )}

      {/* Header */}
      <motion.div variants={fadeInUp} className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Billing</h1>
          <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>
            Manage your subscription and credits
          </p>
        </div>
        {/* Only show Manage Subscription for Stripe subscriptions (not PayPal) */}
        {subscription?.stripe_subscription_id && !subscription.stripe_subscription_id.startsWith('paypal_') && (
          <button
            onClick={handleManageSubscription}
            className="btn-secondary flex items-center space-x-2"
          >
            <ExternalLink className="w-4 h-4" />
            <span>Manage Subscription</span>
          </button>
        )}
      </motion.div>

      {/* Current Usage */}
      <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-purple/10 flex items-center justify-center">
              <Zap className="w-5 h-5 text-accent-purple" />
            </div>
            <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Credits Remaining</span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {usage?.credits_remaining?.toLocaleString() || user?.credits_remaining?.toLocaleString() || 0}
          </p>
        </div>
        <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-cyan/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-accent-cyan" />
            </div>
            <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Current Plan</span>
          </div>
          <p className={`text-3xl font-bold capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>{currentPlan}</p>
        </div>
        <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-accent-green/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-accent-green" />
            </div>
            <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              {subscription?.current_period_end ? 'Renewal Date' : 'Messages This Month'}
            </span>
          </div>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {subscription?.current_period_end
              ? formatDate(subscription.current_period_end)
              : usage?.messages_count?.toLocaleString() || 0}
          </p>
        </div>
      </motion.div>

      {/* Billing Interval Toggle */}
      <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4">
        <span className={`text-sm ${billingInterval === 'monthly' ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-text-muted' : 'text-slate-400')}`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            billingInterval === 'yearly' ? 'bg-accent-purple' : (isDark ? 'bg-dark-600' : 'bg-slate-300')
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
              billingInterval === 'yearly' ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
        <span className={`text-sm ${billingInterval === 'yearly' ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-text-muted' : 'text-slate-400')}`}>
          Yearly <span className="text-accent-green">(Save 17%)</span>
        </span>
      </motion.div>

      {/* Plans */}
      <motion.div variants={fadeInUp}>
        <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Plans</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            return (
            <div
              key={plan.id}
              className={`rounded-2xl p-6 border relative ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'} ${
                isCurrentPlan
                  ? 'border-accent-green ring-2 ring-accent-green/20'
                  : plan.highlighted
                  ? 'border-accent-purple ring-2 ring-accent-purple/20'
                  : ''
              }`}
            >
              {isCurrentPlan ? (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-accent-green text-white text-xs font-medium px-3 py-1 rounded-full">Current Plan</span>
                </div>
              ) : plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="badge-purple text-xs">Most Popular</span>
                </div>
              )}
              <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {plan.name}
              </h3>
              <p className={`text-sm mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{plan.description}</p>
              <div className="flex items-end mb-4">
                <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  ${billingInterval === 'yearly'
                    ? Math.round(plan.price_yearly / 12)
                    : plan.price_monthly}
                </span>
                <span className={`ml-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>/mo</span>
              </div>
              {billingInterval === 'yearly' && plan.price_yearly > 0 && (
                <p className="text-xs text-accent-green mb-3">
                  Billed ${plan.price_yearly}/year
                </p>
              )}
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className={`flex items-center text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}
                  >
                    <Check className="w-4 h-4 text-accent-green mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCurrentPlan || upgrading === plan.id || plan.id === 'free'}
                className={`w-full ${
                  isCurrentPlan
                    ? 'bg-accent-green/20 text-accent-green border border-accent-green/30 cursor-default'
                    : plan.highlighted
                    ? 'btn-primary'
                    : 'btn-secondary'
                } disabled:cursor-not-allowed py-2.5 rounded-xl font-medium transition-all`}
              >
                {upgrading === plan.id ? (
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                ) : isCurrentPlan ? (
                  <span className="flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" />
                    Current Plan
                  </span>
                ) : plan.id === 'free' ? (
                  'Free Forever'
                ) : (
                  'Upgrade'
                )}
              </button>
            </div>
          );
          })}
        </div>
      </motion.div>

      {/* Special Offers */}
      <motion.div variants={fadeInUp}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Student Plan */}
          <Link
            to="/dashboard/student-plan"
            className={`rounded-2xl p-6 border transition-all hover:scale-[1.02] ${isDark ? 'bg-dark-800 border-border hover:border-accent-purple/50' : 'bg-white border-slate-200 shadow-sm hover:shadow-lg hover:border-purple-300'}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-pink flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Student Plan</h3>
                  <span className="text-xs bg-accent-purple/20 text-accent-purple px-2 py-0.5 rounded-full">.edu Required</span>
                </div>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Exclusive pricing for students with valid .edu email
                </p>
              </div>
              <ExternalLink className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
            </div>
          </Link>

          {/* Gift Cards */}
          <Link
            to="/dashboard/gift-cards"
            className={`rounded-2xl p-6 border transition-all hover:scale-[1.02] ${isDark ? 'bg-dark-800 border-border hover:border-accent-pink/50' : 'bg-white border-slate-200 shadow-sm hover:shadow-lg hover:border-pink-300'}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-pink to-accent-orange flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Gift Cards</h3>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Give the gift of AI productivity to friends and colleagues
                </p>
              </div>
              <ExternalLink className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
            </div>
          </Link>
        </div>
      </motion.div>

      {/* Business/Enterprise Contact */}
      <motion.div variants={fadeInUp}>
        <div className={`rounded-2xl p-6 border ${isDark ? 'bg-gradient-to-r from-dark-800 to-dark-700 border-border' : 'bg-gradient-to-r from-slate-50 to-white border-slate-200 shadow-sm'}`}>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-cyan to-accent-purple flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Running a business? Need a custom domain?
              </h3>
              <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                We offer enterprise solutions with white-label options, custom branding, and dedicated support tailored to your business needs.
              </p>
            </div>
            <a
              href="/contact"
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-accent rounded-xl text-white font-medium hover:shadow-glow-sm transition-all flex-shrink-0"
            >
              <Mail className="w-4 h-4" />
              <span>Contact Us</span>
            </a>
          </div>
        </div>
      </motion.div>

      {/* Buy Credits */}
      <motion.div variants={fadeInUp}>
        <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Buy Credits</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { amount: 100, price: 1.99 },
            { amount: 600, price: 9.99 },
            { amount: 2400, price: 39.99, popular: true },
            { amount: 4200, price: 69.99 },
          ].map((pack) => (
            <div
              key={pack.amount}
              onClick={() => handleBuyCredits(pack.amount)}
              className={`rounded-2xl p-6 border cursor-pointer transition-all ${isDark ? 'bg-dark-800 border-border hover:border-white/20' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'} ${
                pack.popular ? 'border-accent-purple' : ''
              } ${buyingCredits === pack.amount ? 'opacity-50 pointer-events-none' : ''}`}
            >
              {pack.popular && (
                <span className="badge-purple text-xs mb-3 inline-block">
                  Best Value
                </span>
              )}
              <p className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {pack.amount.toLocaleString()} credits
              </p>
              <p className="text-lg text-accent-purple">${pack.price}</p>
              {buyingCredits === pack.amount && (
                <RefreshCw className="w-5 h-5 animate-spin mt-2 text-accent-purple" />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <motion.div variants={fadeInUp}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Invoices</h2>
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className={`divide-y ${isDark ? 'divide-dark-700' : 'divide-slate-200'}`}>
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{invoice.number}</p>
                      <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{formatDate(invoice.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`badge ${
                      invoice.status === 'paid'
                        ? 'bg-accent-green/20 text-accent-green'
                        : 'bg-accent-orange/20 text-accent-orange'
                    }`}>
                      {invoice.status}
                    </span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      ${(invoice.amount / 100).toFixed(2)}
                    </span>
                    {invoice.pdf_url && (
                      <a
                        href={invoice.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-purple hover:text-accent-purple/80"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Payment Method Selection Modal */}
      <PaymentMethodModal
        isOpen={showPaymentMethodModal}
        onClose={handleCloseModals}
        onSelectCard={handleSelectCard}
        onSelectPayPal={handleSelectPayPal}
        title={pendingPurchase?.type === 'subscription' ? 'Upgrade Your Plan' : 'Buy Credits'}
        description="Select your preferred payment method"
      />

      {/* PayPal Card Payment Modal */}
      <PayPalCardModal
        isOpen={showCardModal}
        onClose={handleCloseModals}
        onBack={() => {
          setShowCardModal(false);
          setShowPaymentMethodModal(true);
        }}
        onSuccess={handleCardPaymentSuccess}
        purchaseType={pendingPurchase?.type || 'subscription'}
        plan={pendingPurchase?.plan}
        interval={pendingPurchase?.interval}
        creditAmount={pendingPurchase?.creditAmount}
      />
    </motion.div>
  );
}
